import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject
} from "@angular/core";
import { SolverService } from "@app/services/rest/solver.service";
import {
  EventModel,
  PresetManager,
  Scheduler,
  SchedulerConfig
} from "@bryntum/scheduler-thin";
import moment, { Moment } from "moment";
import { UserService } from "@app/services/rest/user.service";
import { ResourceAvailabilityStore } from "@app/services/store/resource-availability-store";
import { ResourceStore } from "@app/services/store/resource-store";
import { ResourceAvailabilityDto } from "@app/models/dto/resource-availability-dto";
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerInputEvent,
  MatDatepickerToggle
} from "@angular/material/datepicker";
import { CustomResourceModel } from "@app/models/bryntum-ext/custom-resource-model";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl
} from "@angular/forms";
import { ScheduleLayout } from "@app/core/schedule/schedule-layout";
import { select, Store } from "@ngrx/store";
import { selectorSchedule } from "@app/core";
import { Subscription } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { AvailabilityDeleteConfirmDialogComponent } from "@app/view/diary/availability-delete-confirm-dialog/availability-delete-confirm-dialog.component";
import { AvailabilityEditorComponent } from "@app/view/diary/availability-editor/availability-editor.component";
import { WorkMode } from "@app/models/rest/resource";
import { BulkAvailabilityWizardComponent } from "@app/view/diary/bulk-availability-wizard/bulk-availability-wizard.component";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { DiaryDefaultsOverview } from "@app/view/diary/diary-viewer/diary-defaults-overview";
import {
  buildSkillText,
  buildZoneText,
  getEffectiveSkills,
  getEffectiveZones
} from "@app/view/planning/schedule-viewer/effective-resource-facts";
import { SkillStore } from "@app/services/store/skill-store";
import { ZoneStore } from "@app/services/store/zone-store";
import { TeamStore } from "@app/services/store/team-store";
import { CollectionFilter } from "@bryntum/core-thin";
import { ColumnStore } from "@bryntum/grid-thin";
import { MatToolbar } from "@angular/material/toolbar";
import { MatAnchor, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatFormField, MatSuffix } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { NgClass } from "@angular/common";
import { RouterLink } from "@angular/router";
import { MatTooltipModule } from "@angular/material/tooltip";

@Component({
  selector: "app-diary-management",
  templateUrl: "./diary-management.component.html",
  styleUrls: ["./diary-management.component.scss"],
  imports: [
    MatToolbar,
    MatIconButton,
    MatIcon,
    MatFormField,
    MatInput,
    FormsModule,
    MatDatepickerInput,
    ReactiveFormsModule,
    MatDatepickerToggle,
    MatSuffix,
    NgClass,
    MatDatepicker,
    MatAnchor,
    MatTooltipModule,
    RouterLink
  ]
})
export class DiaryManagementComponent implements OnInit, OnDestroy {
  protected element = inject(ElementRef);
  solverServer = inject(SolverService);
  userService = inject(UserService);
  resourceAvailabilityStore = inject(ResourceAvailabilityStore);
  resourceStore = inject(ResourceStore);
  dialog = inject(MatDialog);
  protected clientConfig = inject(ClientConfigService);
  protected store = inject<Store<any>>(Store);
  protected skillStore = inject(SkillStore);
  protected zoneStore = inject(ZoneStore);
  protected teamStore = inject(TeamStore);

  public diaryStateChanged: boolean;
  public diaryStateChangeTime: Date;
  public initialized: boolean = false;
  public selectedDate: Moment = moment().isoWeekday(1);
  @Output() public layoutChange = new EventEmitter<boolean>();
  @Output() public initialised = new EventEmitter<boolean>();
  date: UntypedFormControl = new UntypedFormControl(this.selectedDate.toDate());
  stateSubscription: Subscription;
  protected scheduler: Scheduler;
  private plannerId: number;
  private storedData: any;

  static assignAvailabilityFields(
    model: Partial<EventModel>,
    availability: ResourceAvailabilityDto
  ) {
    model.set("id", availability.resourceAvailabilityId);
    model.set("resourceId", availability.resourceId);
    model.set("normalAllowance", availability.normalAllowance);
    model.set("normalAllowanceUsed", availability.normalAllowanceUsed);
    model.set("highAllowance", availability.highAllowance);
    model.set("highAllowanceUsed", availability.highAllowanceUsed);
    model.set("emergencyAllowance", availability.emergencyAllowance);
    model.set("emergencyAllowanceUsed", availability.emergencyAllowanceUsed);
    model.set("normalAllowanceExcess", availability.normalAllowanceExcess);
    model.set("highAllowanceExcess", availability.highAllowanceExcess);
    model.set(
      "emergencyAllowanceExcess",
      availability.emergencyAllowanceExcess
    );
    model.set("startTime", availability.startTime);
    model.set("endTime", availability.endTime);
    model.set("posX", availability.posX);
    model.set("posY", availability.posY);
    model.set("skills", availability.skills || []);
    model.set("zones", availability.zones || []);
  }

  private static loadResourceAvailabilityDto(
    availability: ResourceAvailabilityDto
  ) {
    console.log("loading resource availability dto", availability);

    let eventModel = new EventModel();
    DiaryManagementComponent.assignAvailabilityFields(eventModel, availability);
    eventModel.setStartDate(
      moment(availability.startTime).parseZone().toDate()
    );
    eventModel.setStartEndDate(
      moment(availability.startTime).parseZone().toDate(),
      moment(availability.startTime).parseZone().endOf("day").toDate()
    );
    return eventModel;
  }

  ngOnInit() {
    this.solverServer.updateStream.subscribe(() => {
      this.loadDiaryState();
    });

    // Load filter data first, then proceed with initialization
    this.loadFilterData().then(() => {
      this.loadBaseData().then(() => {
        let resourcePromise = this.resourceStore.loadWithSearch(
          "findAllByPlannerId",
          {
            params: {
              plannerId: this.plannerId,
              projection: "detail"
            },
            pageParams: {
              size: 5000
            }
          },
          true
        );
        this.createPreset();
        this.createScheduler();

        this.initialised.emit(true);
        this.initialized = true;
        this.stateSubscription = this.store
          .pipe(select(selectorSchedule))
          .subscribe((schedule) => {
            console.log("layout state subscription", schedule);
            if (schedule) {
              this.applyScheduleLayout(schedule.layout);
              this.storedData = schedule;
            }
          });
        resourcePromise
          .then((resources) => {
            console.log("resource loaded", this);
            this.handleResourceLoad(resources.resources);

            // Reapply saved filters after resources are loaded
            if (this.storedData && this.storedData.layout) {
              console.log("Reapplying saved filters after resource load");
              this.applyScheduleLayout(this.storedData.layout);

              // Force filter reapplication by triggering a refresh
              setTimeout(() => {
                console.log("Force triggering filter refresh");
                if (
                  this.scheduler &&
                  this.scheduler.resourceStore &&
                  this.storedData.layout.filters
                ) {
                  // Clear and reapply filters to force them to take effect
                  this.scheduler.resourceStore.clearFilters();
                  this.scheduler.resourceStore.filter(
                    this.storedData.layout.filters
                  );
                }
              }, 100);
            }

            this.loadDiaryState();
            this.solverServer.updateStream.subscribe(() => {
              this.diaryStateChanged = true;
              this.diaryStateChangeTime = moment().toDate();
            });
            setInterval(() => {
              if (this.diaryStateChanged) {
                this.diaryStateChanged = false;
                this.diaryStateChanged = null;
                this.loadDiaryState();
              }
            }, 10000);
          })
          .catch((reason) => {
            console.log("resource loading error");
            console.log(reason);
          });
      });
    });
  }

  public loadDiaryState() {
    console.log("loading new diary state");
    this.solverServer.diaryState().then((value) => {
      let newEvents = [];
      value.resourceAvailabilities.forEach((availability) => {
        let existing: Partial<EventModel> = this.scheduler.eventStore.getById(
          availability.resourceAvailabilityId
        );
        if (existing) {
          DiaryManagementComponent.assignAvailabilityFields(
            existing,
            availability
          );
          existing.setStartDate(
            moment(availability.startTime).parseZone().toDate()
          );
          existing.setStartEndDate(
            moment(availability.startTime).parseZone().toDate(),
            moment(availability.startTime).parseZone().endOf("day").toDate()
          );
          return;
        }
        newEvents.push(
          DiaryManagementComponent.loadResourceAvailabilityDto(availability)
        );
      });
      this.scheduler.eventStore.add(newEvents);
    });
  }

  //

  public applyScheduleLayout(scheduleLayout: ScheduleLayout) {
    // console.log("applying layout", scheduleLayout);
    if (scheduleLayout.columns === undefined) {
      return;
    }

    const filters: any[] = (this.scheduler.resourceStore.filters as any).map(
      (filter) => {
        if (filter instanceof CollectionFilter) {
          return {
            value: filter.value,
            property: filter.property,
            operator: filter.operator
          };
        }
        return {};
      }
    );
    try {
      let changed = false;
      if (scheduleLayout.filters.length !== filters.length) {
        changed = true;
      } else {
        for (let i = 0; i < scheduleLayout.filters; i++) {
          if (scheduleLayout.filters[i].value !== filters[i].value) {
            changed = true;
          }
        }
      }
      if (changed) {
        console.log("Applying filters", scheduleLayout.filters);
        this.scheduler.resourceStore.clearFilters();
        this.scheduler.resourceStore.filter(scheduleLayout.filters);
      }
    } catch (e) {}

    console.log(scheduleLayout.sorters);
    if (scheduleLayout.sorters && scheduleLayout.sorters.length > 0) {
      this.scheduler.resourceStore
        .addSorter(scheduleLayout.sorters)
        .then(() => {})
        .catch((reason) => {
          console.log("Cannot add sorters: ", reason, scheduleLayout.sorters);
        });
    }
    scheduleLayout.columns.forEach((column) => {
      if (this.scheduler.columns instanceof ColumnStore) {
        const found = this.scheduler.columns.get(column.name);

        // console.log("found", found);
        if (found) {
          found.width = column.width;
          found.hidden = column.hidden;
        }
      }
    }, this);
  }

  getScheduleLayout(): ScheduleLayout {
    return new ScheduleLayout();
  }

  //
  saveLayout() {
    const layout = this.getScheduleLayout();
    console.log("saving layout", layout);
  }

  public createPreset() {
    PresetManager.registerPreset("divisionGrid", {
      displayDateFormat: "HH:mm",
      shiftIncrement: 1,
      shiftUnit: "day",
      // tickWidth: 250,
      timeResolution: {
        unit: "day",
        increment: 1
      },
      headers: [
        {
          unit: "day",
          increment: 1,
          dateFormat: "dddd"
        },
        {
          unit: "day",
          increment: 1,
          dateFormat: "DD/MM/YYYY"
        }
      ]
    });
  }

  public createScheduler() {
    let me = this;
    const config: Partial<SchedulerConfig> = {
      barMargin: 0,
      rowHeight: 60,
      columnLines: true,
      stateId: "planner-diary-view",
      enableEventAnimations: !this.clientConfig.config.disableAnimation,
      useInitialAnimation: !this.clientConfig.config.disableAnimation,
      allowOverlap: false,
      startDate: moment().startOf("week").add(1, "day").toDate(),
      endDate: moment().endOf("week").add(1, "day").toDate(),
      appendTo: this.element.nativeElement.lastElementChild,
      multiEventSelect: false,
      snapRelativeToEventStartDate: false,
      snap: true,
      // infiniteScroll: false,
      zoomKeepsOriginalTimespan: true,
      scrollable: true,
      forceFit: true,
      autoHeight: false,
      fillTicks: true,
      eventStyle: "minimal",
      // minZoomLevel: 1,
      // maxZoomLevel: 1,
      zoomOnMouseWheel: false,
      zoomOnTimeAxisDoubleClick: false,
      viewPreset: "divisionGrid",
      cls: "sch-container diary-overview-sch-container",
      style: "height: calc(100vh - 88px); width: calc(100vw);",
      eventRenderer: this.renderEvent.bind(this),
      listeners: {
        beforeEventDelete({ eventRecords, context }) {
          // @ts-ignore
          context.async = true;
          let deleteRef = me.dialog.open(
            AvailabilityDeleteConfirmDialogComponent,
            {}
          );
          deleteRef.afterClosed().subscribe((dialogResponse) => {
            console.log("delete dialog response", dialogResponse);
            if (dialogResponse.deleted) {
              me.resourceAvailabilityStore
                .loadSingle(eventRecords[0].get("id"))
                .then((storedAvailability) => {
                  me.resourceAvailabilityStore
                    .delete(storedAvailability)
                    .subscribe(() => {
                      me.scheduler.eventStore.remove(storedAvailability.id);
                    });
                });
            }
          });
          return false;
        },
        eventClick: (event: { eventRecord: EventModel }) => {
          console.log("event click", event.eventRecord, event);
          let dialogRef = this.dialog.open(AvailabilityEditorComponent, {
            panelClass: ["toolbar-popup-dialog", "availability-editor-dialog"],
            data: {
              resourceAvailabilityId: event.eventRecord.get("id")
            }
          });
          dialogRef.afterClosed().subscribe((closed) => {
            if (closed.wasDeleted) {
              me.scheduler.eventStore.remove(event.eventRecord.id);
            }
          });
        }
      },
      columns: [
        {
          id: "resource",
          type: "resourceInfo",
          locked: true,
          draggable: false,
          text: "Resource",
          showEventCount: false,
          showRole: false,
          editor: false,
          width: 240
        },
        {
          id: "team",
          text: "Team",
          width: 200,
          field: "team",
          hidden: true,
          renderer: ({ record }) => {
            if (record.team) {
              return record.team;
            }
            return "";
          },
          filterable: {
            filterField: {
              type: "combo",
              multiSelect: true,
              valueField: "id",
              displayField: "name",
              placeholder: "Filter by teams...",
              store: DiaryDefaultsOverview.getTeamsStore()
            },
            // Custom filtering function to handle the team structure
            filterFn: ({ value, record }) => {
              if (!value || value.length === 0) return true;

              // In diary management, team is stored as a string, so we need to match by name
              if (!record.team) return false;

              // Get team names from the store for the selected IDs
              const selectedTeamNames = value
                .map((teamId) => {
                  const teamRecord =
                    DiaryDefaultsOverview.getTeamsStore().getById(teamId);
                  return teamRecord ? teamRecord.get("name") : null;
                })
                .filter((name) => name !== null);

              return selectedTeamNames.includes(record.team);
            }
          }
        },
        {
          id: "skills",
          width: 360,
          text: "Skills",
          field: "skillText",
          hidden: true,
          renderer: ({ record }) => {
            return record.skillText || "";
          },
          filterable: {
            filterField: {
              type: "combo",
              multiSelect: true,
              valueField: "id",
              displayField: "name",
              placeholder: "Filter by skills...",
              store: DiaryDefaultsOverview.getSkillsStore()
            },
            // Custom filtering function to handle the skillText field
            filterFn: ({ value, record }) => {
              if (!value || value.length === 0) return true;

              if (!record.skillText) return false;

              // Get skill names from the store for the selected IDs
              const selectedSkillNames = value
                .map((skillId) => {
                  const skillRecord =
                    DiaryDefaultsOverview.getSkillsStore().getById(skillId);
                  return skillRecord ? skillRecord.get("name") : null;
                })
                .filter((name) => name !== null);

              console.log(
                "Diary management skill filter - Selected skill names:",
                selectedSkillNames
              );
              console.log(
                "Diary management skill filter - Record skillText:",
                record.skillText
              );

              // Check if any of the selected skills are in the skillText string
              const matches = selectedSkillNames.some((skillName) =>
                record.skillText.includes(skillName)
              );

              console.log("Diary management skill filter - Matches:", matches);
              return matches;
            }
          }
        },
        {
          id: "zones",
          text: "Zones",
          field: "zoneText",
          hidden: true,
          width: 200,
          renderer: ({ record }) => {
            return record.zoneText || "";
          },
          filterable: {
            filterField: {
              type: "combo",
              multiSelect: true,
              valueField: "id",
              displayField: "name",
              placeholder: "Filter by zones...",
              store: DiaryDefaultsOverview.getZonesStore()
            },
            // Custom filtering function to handle the zoneText field
            filterFn: ({ value, record }) => {
              if (!value || value.length === 0) return true;

              if (!record.zoneText) return false;

              // Get zone names from the store for the selected IDs
              const selectedZoneNames = value
                .map((zoneId) => {
                  const zoneRecord =
                    DiaryDefaultsOverview.getZonesStore().getById(zoneId);
                  return zoneRecord ? zoneRecord.get("name") : null;
                })
                .filter((name) => name !== null);

              // Check if any of the selected zones are in the zoneText string
              return selectedZoneNames.some((zoneName) =>
                record.zoneText.includes(zoneName)
              );
            }
          }
        },
        {
          id: "workMode",
          text: "Mode",
          field: "workMode",
          hidden: true,
          width: 50,
          renderer: ({ record }) =>
            record.workMode && record.workMode === WorkMode.Offline
              ? "Offline"
              : "Online"
        }
      ],
      features: {
        rowCopyPaste: false,
        eventCopyPaste: false,
        eventDrag: false,
        eventDragCreate: false,
        stickyEvents: false,
        scheduleMenu: {
          items: {
            addEvent: false
          }
        },
        timeAxisHeaderMenu: {
          items: {
            zoomLevel: false,
            dateRange: false
          }
        },
        eventMenu: {
          items: {
            deleteEvent: false,
            unassignEvent: false
          }
        },
        eventEdit: false,
        columnResize: true,
        regionResize: true,
        eventFilter: false,
        eventResize: false,
        eventTooltip: {
          allowOver: true,
          template: ({ eventRecord }) => this.renderEventTooltip(eventRecord)
        },
        // group: 'team',
        scheduleTooltip: false,
        timeRanges: { showHeaderElements: false, showCurrentTimeLine: false },
        resourceTimeRanges: false,
        cellEdit: false,
        filter: {
          pickerConfig: {
            showAddFilterButton: false,
            dateFormat: "DD/MM/YYYY",
            canDeleteFilter: () => false
          }
        },
        headerMenu: true
      }
    };
    this.scheduler = new Scheduler(config);
  }

  public loadBaseData(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      let plannerPromise = this.userService.getPlanner();
      plannerPromise
        .then((planner) => {
          // this.teamList = planner.teamList;
          this.plannerId = planner.id;
        })
        .catch((reason) => reject(reason));
      Promise.all([plannerPromise])
        .then(() => resolve(true))
        .catch((reason) => reject(reason));
    });
  }

  handleResourceLoad(resources) {
    resources.forEach((resource) => {
      let current = this.scheduler.resourceStore.getById(resource.id);
      if (!current) {
        current = new CustomResourceModel({
          id: resource.id,
          name: resource.name
        });
        (current as CustomResourceModel).setResourceImageUrl(resource.imageUrl);
        current.set("entity", resource);
        current.set("team", "No Team");
        current.set("skillText", "");

        current.set("workMode", resource.workMode);
        this.scheduler.resourceStore.add(current);
        current = this.scheduler.resourceStore.getById(resource.id);
      }
      (current as CustomResourceModel).setResourceImageUrl(resource.imageUrl);
      current.set("name", resource.name);
      current.set("entity", resource);
      if (resource.team) {
        current.set("team", resource.team.name);
      }
      this.applyEffectiveFactTexts(current, resource);
    });
  }

  /**
   * Sets skillText/zoneText restricted to the assignments active for the
   * week in view (assignment start/end dates and mapping-pattern windows —
   * #1558). Both the columns and their filters read these fields.
   */
  private applyEffectiveFactTexts(record: any, resource: any) {
    const viewStart = this.scheduler?.startDate as Date;
    const viewEnd = this.scheduler?.endDate as Date;
    record.set(
      "skillText",
      buildSkillText(
        getEffectiveSkills(resource.skillsList, viewStart, viewEnd)
      )
    );
    record.set(
      "zoneText",
      buildZoneText(getEffectiveZones(resource.zonesList, viewStart, viewEnd))
    );
  }

  /** Recomputes effective skills/zones after the week in view changes. */
  private refreshEffectiveResourceFacts() {
    if (!this.scheduler) {
      return;
    }
    const resourceStore: any = this.scheduler.resourceStore;
    resourceStore.allRecords.forEach((record: any) => {
      const resource = record.get("entity");
      if (resource) {
        this.applyEffectiveFactTexts(record, resource);
      }
    });
    if (resourceStore.isFiltered) {
      resourceStore.filter();
    }
  }

  renderEvent(display: { eventRecord: EventModel; renderData }) {
    let start = moment(display.eventRecord.get("startTime")).parseZone();
    let end = moment(display.eventRecord.get("endTime")).parseZone();
    let duration = end.diff(start, "seconds");
    let overallUse =
      display.eventRecord.get("emergencyAllowanceUsed") / duration;
    let normalUse =
      display.eventRecord.get("normalAllowanceUsed") /
      display.eventRecord.get("normalAllowance");
    let highUse =
      display.eventRecord.get("highAllowanceUsed") /
      display.eventRecord.get("highAllowance");
    let emergencyUse =
      display.eventRecord.get("emergencyAllowanceUsed") /
      display.eventRecord.get("emergencyAllowance");

    let error =
      overallUse > 1 || normalUse > 1 || highUse > 1 || emergencyUse > 1;
    display.renderData.style = `background-color:transparent`;
    display.renderData.eventStyle = `border-radius: 0px;`;
    const statBarHeight = 48;
    const statBarBottom = 4;
    return `
      ${this.renderStatusBar(60, 140, error, 0)}
      ${this.renderStatBar(
        statBarHeight,
        68,
        "Blue",
        overallUse,
        statBarBottom
      )}
      ${this.renderStatBar(
        statBarHeight,
        48,
        "green",
        normalUse,
        statBarBottom
      )}
      ${this.renderStatBar(
        statBarHeight,
        28,
        "darkorange",
        highUse,
        statBarBottom
      )}
      ${this.renderStatBar(
        statBarHeight,
        8,
        "red",
        emergencyUse,
        statBarBottom
      )}

<div style="width:${display.renderData.width}px; height: calc(100% - 4px);
    box-sizing: border-box; padding: 8px 12px; margin:2px; background-color: ${
      error ? "#f55e5e" : "#66BB6A"
    }">
      ${start.format("HH:mm")}
      -${end.parseZone().format("HH:mm")}
      <br/>
      ${moment.duration(duration, "seconds").humanize(false)}
      </div>
    `;
  }

  renderStatusBar(height, position, error, bottom) {
    let heightPx = height + "px";
    return `

<div class="status-bar" style="position:absolute;
  left: 0;
  height: ${heightPx};
  bottom: ${bottom};
  width: 5px;
  background-color:${error ? "red" : "darkgreen"};"></div>

`;
  }

  renderStatBar(
    height,
    position,
    color,
    usagePercentage: number,
    bottom: number
  ) {
    let usage = Math.round(usagePercentage * 100);
    if (Number.isNaN(usage) || !Number.isFinite(usage)) {
      return `<div class="stat-bar" style="position:absolute;
  right: ${position}px;
  height: ${height}px;
  bottom: ${bottom}px;
  margin-top:2px;
  margin-bottom:2px;
  width: 18px;
  background-color:#4c0d0d;"></div>

   <div class="stat-text" style="position:absolute;
  right: ${position - 8}px;
  bottom:${bottom + 15}px;
  transform: rotate(270deg);
  font-size: 13px;
  font-weight: bold;
  color: white;
  width: 34px;">Over</div>`;
    }
    return `
<div class="stat-bar" style="position:absolute;
  right: ${position}px;
  height: ${height}px;
  bottom: ${bottom}px;
  margin-top:2px;
  margin-bottom:2px;
  width: 18px;
  background-color:${usagePercentage > 1 ? "red" : "black"};"></div>

<div class="stat-bar" style="position:absolute;
  right: ${position + 2}${"px"};
  bottom:${bottom + 2}px;
  height: ${(height - 4) * (usagePercentage < 1 ? usagePercentage : 1)}px;
  width: 14px;
  background-color:${color};"></div>

  <div class="stat-text" style="position:absolute;
  right: ${position - 8}px;
  bottom:${bottom + 15}${"px"};
  transform: rotate(270deg);
  font-size: 13px;
  font-weight: bold;
  color: white;
  width: 34px;">${usage}%</div>
`;
  }

  previousWeek() {
    this.scheduler.setStartDate(
      moment(this.scheduler.startDate).subtract(7, "days").toDate(),
      true
    );
    this.selectedDate = moment(this.scheduler.startDate);
    this.date.setValue(this.selectedDate.toDate());
    this.refreshEffectiveResourceFacts();
  }

  selectDate($event: MatDatepickerInputEvent<unknown>) {
    this.scheduler.setStartDate(
      moment($event.value).isoWeekday(1).toDate(),
      true
    );
    this.selectedDate = moment(this.scheduler.startDate);
    this.date.setValue(this.selectedDate.toDate());
    this.refreshEffectiveResourceFacts();
  }

  nextWeek() {
    this.scheduler.setStartDate(
      moment(this.scheduler.startDate).add(7, "days").toDate(),
      true
    );
    this.selectedDate = moment(this.scheduler.startDate);
    this.date.setValue(this.selectedDate.toDate());
    this.refreshEffectiveResourceFacts();
  }

  ngOnDestroy(): void {
    if (this.stateSubscription) {
      this.stateSubscription.unsubscribe();
    }
  }

  openBulkWizard() {
    console.log("opening bulk wizard");
    this.dialog.open(BulkAvailabilityWizardComponent, { disableClose: true });
  }

  /**
   * Renders the tooltip content for an event
   * @param eventRecord The event record to render the tooltip for
   * @returns HTML string for the tooltip
   */
  renderEventTooltip(eventRecord: EventModel): string {
    const skills = eventRecord.get("skills") || [];
    const zones = eventRecord.get("zones") || [];

    // Get resource to access skill and zone text information
    const resourceId = eventRecord.get("resourceId");
    const resource = this.scheduler.resourceStore.getById(resourceId);

    // Format start and end times
    const start = moment(eventRecord.get("startTime")).parseZone();
    const end = moment(eventRecord.get("endTime")).parseZone();

    // Get skill and zone information from the resource if available
    let skillsInfo = "";
    let zonesInfo = "";

    if (
      resource &&
      resource.get("entity") &&
      resource.get("entity").skillsList
    ) {
      // Filter resource skills to only include those in the event's skills array
      const resourceSkills = resource.get("entity").skillsList || [];
      const filteredSkills = resourceSkills.filter((skill) =>
        skills.includes(skill.skill.id)
      );

      if (filteredSkills.length > 0) {
        const skillNames = filteredSkills
          .map((skill) => skill.skill.description)
          .sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          );

        skillsInfo = `
          <div class="tooltip-section">
            <h4>Skills:</h4>
            <ul>
              ${skillNames.map((name) => `<li>${name}</li>`).join("")}
            </ul>
          </div>
        `;
      }
    }

    // Get zone names from the resource's zoneText if available
    if (resource && resource.get("zoneText")) {
      zonesInfo = `
        <div class="tooltip-section">
          <h4>Zones:</h4>
          <ul>
            ${resource
              .get("zoneText")
              .split(", ")
              .map((name) => `<li>${name}</li>`)
              .join("")}
          </ul>
        </div>
      `;
    } else if (
      resource &&
      resource.get("entity") &&
      resource.get("entity").zonesList
    ) {
      // Try to filter resource zones to only include those in the event's zones array
      const resourceZones = resource.get("entity").zonesList || [];

      // Try different ways to match zones
      let filteredZones = resourceZones.filter((zone) => {
        // Try to match by id or zoneId property
        return (
          zones.includes(zone.id) ||
          zones.includes(zone.zoneId) ||
          zones.includes(zone.zone?.id)
        );
      });

      if (filteredZones.length > 0) {
        const zoneNames = filteredZones
          .map((zone) => zone.zoneName || zone.name || zone.zone?.name)
          .filter((name) => name) // Filter out undefined/null values
          .sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          );

        if (zoneNames.length > 0) {
          zonesInfo = `
            <div class="tooltip-section">
              <h4>Zones:</h4>
              <ul>
                ${zoneNames.map((name) => `<li>${name}</li>`).join("")}
              </ul>
            </div>
          `;
        }
      }
    }

    // If no specific skills or zones are found, show the raw IDs
    if (!skillsInfo && skills.length > 0) {
      skillsInfo = `
        <div class="tooltip-section">
          <h4>Skill IDs:</h4>
          <ul>
            ${skills.map((id) => `<li>${id}</li>`).join("")}
          </ul>
        </div>
      `;
    }

    if (!zonesInfo && zones.length > 0) {
      zonesInfo = `
        <div class="tooltip-section">
          <h4>Zone IDs:</h4>
          <ul>
            ${zones.map((id) => `<li>${id}</li>`).join("")}
          </ul>
        </div>
      `;
    }

    return `
      <div class="event-tooltip" style="max-height: 300px; overflow-y: auto;">
        <div class="tooltip-header">
          <h3>Resource Availability</h3>
          <div class="tooltip-time">
            ${start.format("HH:mm")} - ${end.format("HH:mm")}
          </div>
          <div class="tooltip-duration">
            Duration: ${moment.duration(end.diff(start)).humanize()}
          </div>
        </div>

        ${skillsInfo}
        ${zonesInfo}

        <div class="tooltip-section">
          <h4>Allowances:</h4>
          <div class="allowance-item">
            <span>Normal:</span> ${this.formatAllowance(
              eventRecord.get("normalAllowance"),
              eventRecord.get("normalAllowanceUsed")
            )}
          </div>
          <div class="allowance-item">
            <span>High:</span> ${this.formatAllowance(
              eventRecord.get("highAllowance"),
              eventRecord.get("highAllowanceUsed")
            )}
          </div>
          <div class="allowance-item">
            <span>Emergency:</span> ${this.formatAllowance(
              eventRecord.get("emergencyAllowance"),
              eventRecord.get("emergencyAllowanceUsed")
            )}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Format allowance values for display, converting minutes to hours and minutes
   */
  private formatAllowance(allowance: number, used: number): string {
    if (allowance === undefined || allowance === null) return "N/A";

    // Convert seconds to hours and minutes
    const allowanceMinutes = Math.round(allowance / 60);
    const allowanceHours = Math.floor(allowanceMinutes / 60);
    const allowanceRemainingMinutes = allowanceMinutes % 60;

    const formattedAllowance =
      allowanceHours > 0
        ? `${allowanceHours}h ${
            allowanceRemainingMinutes > 0 ? allowanceRemainingMinutes + "m" : ""
          }`
        : `${allowanceMinutes}m`;

    if (used === undefined || used === null) {
      return formattedAllowance;
    }

    // Convert used seconds to hours and minutes
    const usedMinutes = Math.round(used / 60);
    const usedHours = Math.floor(usedMinutes / 60);
    const usedRemainingMinutes = usedMinutes % 60;

    const formattedUsed =
      usedHours > 0
        ? `${usedHours}h ${
            usedRemainingMinutes > 0 ? usedRemainingMinutes + "m" : ""
          }`
        : `${usedMinutes}m`;

    const percentage = allowance > 0 ? Math.round((used / allowance) * 100) : 0;

    return `${formattedUsed} / ${formattedAllowance} (${percentage}%)`;
  }

  private async loadFilterData() {
    try {
      // Load skills, zones, and teams in parallel
      const [skillsResult, zonesResult, teamsResult] = await Promise.all([
        this.skillStore.loadAll(),
        this.zoneStore.loadAll(),
        this.teamStore.loadAll()
      ]);

      console.log("Diary Management - Skills loaded:", skillsResult);
      console.log("Diary Management - Zones loaded:", zonesResult);
      console.log("Diary Management - Teams loaded:", teamsResult);

      // Set the filter data in DiaryDefaultsOverview before scheduler creation
      DiaryDefaultsOverview.setFilterData(
        skillsResult.resources || [],
        zonesResult.resources || [],
        teamsResult.resources || []
      );

      console.log("Diary Management filter data set in DiaryDefaultsOverview");
    } catch (error) {
      console.error("Error loading diary management filter data:", error);
    }
  }
}
